import { useContext, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  DevHubBox,
  DevHubCircularProgress,
  DevHubDynamicIcon,
  DevHubIconButton,
  DevHubTooltip,
} from '@devxdiscover/devhub-ui';
import { SystemContext } from '../../contexts/SystemContext';
import {
  BusinessCapabilitiesSelect,
  BusinessCapability,
} from '../common/BusinessCapabilitiesSelect';
import { EditAppMutation } from './queries/InsightsQueries';
import AppInfoItem from '../common/AppInfoItem';

interface Props {
  applicationId: string;
  businessCapabilities: BusinessCapability[];
  edit: boolean;
  hasEditPermission: boolean;
}

/**
 * Editable business capabilities field for the Insights About page.
 * Displays current capabilities and allows multi-select editing.
 */
export const EditableBusinessCapabilities = ({
  applicationId,
  businessCapabilities,
  edit,
  hasEditPermission,
}: Props) => {
  const { GetApplicationQuery, applicationVariables } =
    useContext(SystemContext);
  const [editField, setEditField] = useState(false);
  const [selectedCapabilities, setSelectedCapabilities] = useState<
    BusinessCapability[]
  >(businessCapabilities ?? []);

  const [editApp, { loading }] = useMutation(EditAppMutation, {
    refetchQueries: [
      {
        query: GetApplicationQuery,
        variables: applicationVariables,
      },
      'GetApplicationDetails',
    ],
  });

  const handleEdit = () => {
    setSelectedCapabilities(businessCapabilities ?? []);
    setEditField(prev => !prev);
  };

  const handleClose = () => {
    setEditField(false);
    setSelectedCapabilities(businessCapabilities ?? []);
  };

  const handleSubmit = () => {
    editApp({
      variables: {
        input: {
          applicationId: Number(applicationId),
          iopBusinessCapabilities: selectedCapabilities.map(bc => bc.sysId),
        },
      },
    });
    setEditField(false);
  };

  const displayText =
    businessCapabilities && businessCapabilities.length > 0
      ? businessCapabilities.map(bc => bc.name).join(', ')
      : 'None';

  return (
    <DevHubBox display="flex">
      {edit && editField ? (
        <DevHubBox flex={1}>
          <BusinessCapabilitiesSelect
            selectedCapabilities={selectedCapabilities}
            setSelectedCapabilities={setSelectedCapabilities}
            helperText="Select one or more business capabilities"
            required={false}
          />
        </DevHubBox>
      ) : (
        <AppInfoItem
          primaryText="Business Capabilities:"
          secondaryText={displayText}
        />
      )}
      {edit && !editField && (
        <>
          {loading ? (
            <DevHubCircularProgress thickness={6} size={22} />
          ) : (
            <DevHubTooltip title="Edit">
              <DevHubBox>
                <DevHubIconButton
                  size="medium"
                  disabled={!hasEditPermission}
                  onClick={handleEdit}
                >
                  <DevHubDynamicIcon iconName="Edit" />
                </DevHubIconButton>
              </DevHubBox>
            </DevHubTooltip>
          )}
        </>
      )}
      {edit && editField && (
        <DevHubBox display="inline-flex" ml={4} pt={2}>
          <DevHubTooltip title="Cancel">
            <DevHubBox>
              <DevHubIconButton onClick={handleClose}>
                <DevHubDynamicIcon iconName="CancelOutlined" />
              </DevHubIconButton>
            </DevHubBox>
          </DevHubTooltip>
          <DevHubTooltip title="Save">
            <DevHubBox>
              <DevHubIconButton onClick={handleSubmit}>
                <DevHubDynamicIcon iconName="Save" />
              </DevHubIconButton>
            </DevHubBox>
          </DevHubTooltip>
        </DevHubBox>
      )}
    </DevHubBox>
  );
};
